/**
 * Ventas — lista de órdenes y wizard "Nueva venta" en pasos.
 *
 * El wizard tiene 5 pasos lineales (con Atrás / Siguiente):
 *
 *   1. Cliente + Combos
 *        ¿Quieres agregar combos? Si/No. Si sí, puedes agregar N combos
 *        diferentes. Cada combo queda expandido en sus tallos y puedes
 *        editar cantidades y precios por línea antes de avanzar.
 *   2. Items individuales
 *        ¿Quieres agregar items? Si/No. Si sí, agregas lineas (variedad,
 *        cantidad, longitud, precio).
 *   3. Empaque (cajas)
 *        ¿Quieres definir cajas? Si/No. Si sí, declaras cuantas cajas, el
 *        tipo y cuantos tallos van por caja — incluyendo qué variedades.
 *   4. Overview de cajas
 *        Vista de solo lectura: lo que va en cada caja (desde el paso 3)
 *        + resumen de tallos totales y mismatches.
 *   5. Precios y confirmación
 *        Tabla con cada linea editable (precio/tallo); total actualizado en
 *        vivo; botón "Crear venta".
 *
 * El modelo de datos que el wizard produce:
 *   payload = {
 *     clientId, date, totalUSD,
 *     combos: [{comboId, multiplier}],   // meta informativa
 *     items: [{variety, quantity, stemLength, priceUSD, fromCombo?}],  // AUTORITATIVO
 *     boxes: [{boxSize, stemsPerBox, boxCount, items:[{variety,quantity,stemLength}]}]
 *   }
 *
 * El backend usa items[] como la lista unica de tallos (ver flattenSaleItems),
 * por lo que combos[] y boxes[] no se re-suman.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  PREDEFINED_COMBOS,
  BOX_SIZES,
  SALE_STATUS_LABELS,
} from '@expogreens/shared'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { ExpandableCards, CardField, CardGrid } from '@/components/ui/ExpandableCards'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'
import { fmtDate, fmtMoney, cn } from '@/lib/utils'

/**
 * Acceso robusto a las fechas de una venta. El wizard legacy guarda un solo
 * campo `date`, mientras que el seed/datos nuevos usan `saleDate` y
 * `deliveryDate` por separado. Para mostrar y editar, centralizamos la
 * extracción: `deliveryDate` cae a `saleDate` → `date` si no hay nada mejor.
 */
const getSaleDate = (s) => s?.saleDate || s?.date || ''
const getDeliveryDate = (s) => s?.deliveryDate || s?.saleDate || s?.date || ''

// Estilos por estado del pedido (alineados con los nuevos labels en es).
const STATUS_PILL = {
  confirmed: 'bg-gray-100 text-gray-700',
  supplying: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-200 text-emerald-900',
  cancelled: 'bg-red-100 text-red-700',
  // legacy
  pending: 'bg-gray-100 text-gray-700',
  in_preparation: 'bg-blue-100 text-blue-800',
}

const STEPS = [
  { id: 1, label: 'Cliente + combos' },
  { id: 2, label: 'Items individuales' },
  { id: 3, label: 'Empaque' },
  { id: 4, label: 'Revisar cajas' },
  { id: 5, label: 'Precios y confirmar' },
]

const EMPTY_ITEM = { variety: '', quantity: 0, stemLength: 60, priceUSD: 0, fromCombo: null }

/**
 * Build a key that identifies a line by variety + stem length. Used to group
 * pending stems against packed stems in the Step 3 "pendiente por empacar".
 */
const lineKey = (variety, stemLength) => `${variety}|${Number(stemLength) || 0}`

function flattenSaleForDisplay(sale) {
  // New rule: items[] is authoritative when present.
  const authoritative = (sale.items || []).filter((it) => (Number(it.quantity) || 0) > 0)
  if (authoritative.length > 0) {
    return authoritative.map((it) => ({
      source: it.fromCombo ? `combo ${it.fromCombo}` : 'item',
      variety: it.variety,
      stemLength: it.stemLength,
      quantity: Number(it.quantity) || 0,
      priceUSD: Number(it.priceUSD) || 0,
    }))
  }
  // Legacy fallback
  const lines = []
  for (const box of sale.boxes || []) {
    for (const it of box.items || []) {
      lines.push({
        source: `box×${box.boxCount}`,
        variety: it.variety,
        stemLength: it.stemLength,
        quantity: (Number(it.quantity) || 0) * (Number(box.boxCount) || 1),
        priceUSD: Number(it.priceUSD) || 0,
      })
    }
  }
  if (sale.comboId) {
    const combo = PREDEFINED_COMBOS.find((c) => c.id === sale.comboId)
    const mult = Number(sale.comboMultiplier) || 1
    if (combo) {
      for (const it of combo.items) {
        lines.push({
          source: `combo ${combo.name}×${mult}`,
          variety: it.variety,
          stemLength: it.stemLength,
          quantity: it.quantity * mult,
          priceUSD: 0,
        })
      }
    }
  }
  return lines
}

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────
export default function SalesPage() {
  const sales = useApi('/sales')
  const clients = useApi('/clients')
  const varieties = useApi('/varieties')
  // Catálogo completo de combos (preestablecidos + custom desde el
  // módulo Producto). Cada combo trae su `defaultPacking` que se aplica
  // automáticamente en el Step 3.
  const combosApi = useApi('/combos')

  const [creating, setCreating] = useState(false)

  // Wizard state ─────────────────────────────────────────────────────
  const [step, setStep] = useState(1)
  // En Cliente se pondría el Principal; si el principal tiene
  // consignatarios, se puede elegir uno como destino real del despacho.
  const [principalClientId, setPrincipalClientId] = useState('')
  const [consigneeClientId, setConsigneeClientId] = useState('')
  // clientId efectivo es el consignatario si fue seleccionado, sino el principal.
  const clientId = consigneeClientId || principalClientId
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  // Por feedback: la fecha y hora de despacho se solicitan en el primer
  // paso (Compras las necesita para priorizar). Antes solo aparecían al
  // editar.
  const [deliveryDate, setDeliveryDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  )
  const [dispatchTime, setDispatchTime] = useState('')

  // Step 1: combos
  const [wantCombos, setWantCombos] = useState(null) // null | true | false
  const [comboEntries, setComboEntries] = useState([])
  // each entry: { comboId, multiplier, items: [{variety,quantity,stemLength,priceUSD}] }

  // Step 2: individual items
  const [wantItems, setWantItems] = useState(null) // null | true | false
  const [items, setItems] = useState([{ ...EMPTY_ITEM }])

  // Step 3: packing — each entry has exactly one item (variety+stemLength)
  // so the remaining-stems tracker stays simple.
  const [wantBoxes, setWantBoxes] = useState(null) // null | true | false
  const [boxes, setBoxes] = useState([])

  // Return-modal state (sobre una venta existente)
  const [returnFor, setReturnFor] = useState(null)
  const [returnReason, setReturnReason] = useState('')
  const [returnItems, setReturnItems] = useState([])

  // Edit-modal state (sobre una venta existente). Una venta siempre se
  // puede editar — incluso si ya está cerrada (cancelled/delivered); el
  // usuario puede, por ejemplo, corregir la fecha de entrega o una
  // longitud que quedó mal registrada.
  const [editFor, setEditFor] = useState(null)

  // Filtro por cliente en el listado. `filterClientId` vacío = sin filtro.
  // `filterIncludeChildren` es útil para mayoristas con dropshipping: si el
  // mayorista tiene clientes finales ligados (parentClientId=mayorista.id),
  // el filtro puede incluir también las ventas hechas al cliente final.
  const [filterClientId, setFilterClientId] = useState('')
  const [filterIncludeChildren, setFilterIncludeChildren] = useState(true)

  const create = useMutation((payload) => apiCall('/sales', { method: 'POST', body: payload }))
  const cancel = useMutation((id) =>
    apiCall(`/sales/${id}`, { method: 'PUT', body: { status: 'cancelled' } }),
  )
  const update = useMutation(({ id, payload }) =>
    apiCall(`/sales/${id}`, { method: 'PUT', body: payload }),
  )
  const remove = useMutation((id) => apiCall(`/sales/${id}`, { method: 'DELETE' }))
  const createReturn = useMutation((payload) =>
    apiCall('/returns', { method: 'POST', body: payload }),
  )

  // ── Filtrado por cliente ──────────────────────────────────────────
  // El mayorista hace dropshipping → quiere poder filtrar sus ventas Y
  // también las de sus clientes finales (hijos). Calculamos los ids a
  // considerar: el propio + (opcionalmente) todos los hijos con
  // parentClientId === filterClientId.
  const filterClientIds = useMemo(() => {
    if (!filterClientId) return null
    const ids = new Set([filterClientId])
    if (filterIncludeChildren) {
      for (const c of clients.data || []) {
        if (c.parentClientId === filterClientId) ids.add(c.id)
      }
    }
    return ids
  }, [filterClientId, filterIncludeChildren, clients.data])

  const visibleSales = useMemo(() => {
    if (!Array.isArray(sales.data)) return []
    if (!filterClientIds) return sales.data
    return sales.data.filter((s) => filterClientIds.has(s.clientId))
  }, [sales.data, filterClientIds])

  const selectedClient = useMemo(
    () => (clients.data || []).find((c) => c.id === filterClientId) || null,
    [clients.data, filterClientId],
  )
  const selectedClientHasChildren = useMemo(
    () =>
      filterClientId &&
      (clients.data || []).some((c) => c.parentClientId === filterClientId),
    [clients.data, filterClientId],
  )

  // ── Derived data ─────────────────────────────────────────────────
  // Flat list of ALL lines that define stems to sell (combos expanded + items).
  const allLines = useMemo(() => {
    const result = []
    if (wantCombos) {
      for (const entry of comboEntries) {
        for (const line of entry.items) {
          if (!line.variety) continue
          result.push({ ...line, fromCombo: entry.comboLabel })
        }
      }
    }
    if (wantItems) {
      for (const it of items) {
        if (!it.variety) continue
        result.push({ ...it, fromCombo: null })
      }
    }
    return result
  }, [wantCombos, comboEntries, wantItems, items])

  const totalStems = useMemo(
    () => allLines.reduce((a, l) => a + (Number(l.quantity) || 0), 0),
    [allLines],
  )
  const totalUSD = useMemo(
    () =>
      allLines.reduce(
        (a, l) => a + (Number(l.quantity) || 0) * (Number(l.priceUSD) || 0),
        0,
      ),
    [allLines],
  )
  const totalBoxedStems = useMemo(() => {
    if (!wantBoxes) return 0
    return boxes.reduce((a, b) => {
      const per = (b.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0)
      return a + per * (Number(b.boxCount) || 1)
    }, 0)
  }, [wantBoxes, boxes])

  // ── Reset ────────────────────────────────────────────────────────
  const resetForm = () => {
    setStep(1)
    setPrincipalClientId('')
    setConsigneeClientId('')
    setDate(new Date().toISOString().slice(0, 10))
    setDeliveryDate(new Date().toISOString().slice(0, 10))
    setDispatchTime('')
    setWantCombos(null)
    setComboEntries([])
    setWantItems(null)
    setItems([{ ...EMPTY_ITEM }])
    setWantBoxes(null)
    setBoxes([])
  }

  // ── Combo handlers (step 1) ──────────────────────────────────────
  const addComboEntry = (comboId) => {
    // Busca primero en el catálogo cargado del backend (incluye custom).
    // Fallback a PREDEFINED para no romper si /combos aún no resolvió.
    const all = combosApi.data || PREDEFINED_COMBOS
    const combo = all.find((c) => c.id === comboId)
    if (!combo) return
    setComboEntries((l) => [
      ...l,
      {
        comboId: combo.id,
        comboLabel: combo.name,
        multiplier: 1,
        // Empaque por defecto definido en el módulo Producto. Si el
        // combo no lo trae (compat), cae a fullbox × 1.
        defaultPacking: combo.defaultPacking || { boxSize: 'fullbox', boxCount: 1 },
        items: combo.items.map((it) => ({
          variety: it.variety,
          quantity: it.quantity,
          stemLength: it.stemLength,
          priceUSD: 0,
        })),
      },
    ])
  }
  const removeComboEntry = (idx) =>
    setComboEntries((l) => l.filter((_, i) => i !== idx))
  const patchComboLine = (ei, li, patch) =>
    setComboEntries((l) =>
      l.map((e, i) =>
        i !== ei
          ? e
          : { ...e, items: e.items.map((it, j) => (j === li ? { ...it, ...patch } : it)) },
      ),
    )
  const patchComboMultiplier = (ei, newMult) =>
    setComboEntries((l) =>
      l.map((e, i) => {
        if (i !== ei) return e
        const prev = Number(e.multiplier) || 1
        const next = Math.max(1, Number(newMult) || 1)
        if (prev === next) return { ...e, multiplier: next }
        // Rescale quantities proportionally so the user doesn't have to re-enter.
        return {
          ...e,
          multiplier: next,
          items: e.items.map((it) => ({
            ...it,
            quantity: Math.round(((Number(it.quantity) || 0) / prev) * next),
          })),
        }
      }),
    )

  // ── Item handlers (step 2) ───────────────────────────────────────
  const addItem = () => setItems((l) => [...l, { ...EMPTY_ITEM }])
  const removeItem = (i) =>
    setItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))
  const patchItem = (i, patch) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))

  // ── Box handlers (step 3) ────────────────────────────────────────
  // Una caja ahora puede contener MÚLTIPLES variedades. Cada `box.items`
  // es un array de { variety, stemLength, quantity }, y `stemsPerBox` se
  // calcula como la suma de las quantities. `boxCount` repite el mismo
  // contenido N veces (M cajas idénticas con la misma mezcla).
  const addPacking = ({ boxSize, boxCount, items }) =>
    setBoxes((l) => [
      ...l,
      {
        boxSize,
        boxCount: Number(boxCount) || 1,
        stemsPerBox: items.reduce((a, it) => a + (Number(it.quantity) || 0), 0),
        items: items.map((it) => ({
          variety: it.variety,
          stemLength: Number(it.stemLength) || 0,
          quantity: Number(it.quantity) || 0,
        })),
      },
    ])
  const updatePacking = (idx, { boxSize, boxCount, items }) =>
    setBoxes((l) =>
      l.map((b, i) =>
        i === idx
          ? {
              boxSize,
              boxCount: Number(boxCount) || 1,
              stemsPerBox: items.reduce(
                (a, it) => a + (Number(it.quantity) || 0),
                0,
              ),
              items: items.map((it) => ({
                variety: it.variety,
                stemLength: Number(it.stemLength) || 0,
                quantity: Number(it.quantity) || 0,
              })),
            }
          : b,
      ),
    )
  const removeBox = (i) => setBoxes((l) => l.filter((_, idx) => idx !== i))

  // Bulk-update priceUSD for all lines matching a variety+stemLength
  // (used from step 5 to edit price in one place).
  const setPriceForLine = (index, newPrice) => {
    // `index` refers to allLines order. We need to figure out whether the line
    // came from a combo entry or from items[]. We iterate in the same order.
    let seen = 0
    if (wantCombos) {
      for (let ei = 0; ei < comboEntries.length; ei++) {
        const entry = comboEntries[ei]
        for (let li = 0; li < entry.items.length; li++) {
          if (!entry.items[li].variety) continue
          if (seen === index) {
            patchComboLine(ei, li, { priceUSD: newPrice })
            return
          }
          seen++
        }
      }
    }
    if (wantItems) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].variety) continue
        if (seen === index) {
          patchItem(i, { priceUSD: newPrice })
          return
        }
        seen++
      }
    }
  }

  // ── Navigation ───────────────────────────────────────────────────
  const canAdvance = () => {
    if (step === 1) {
      if (!clientId) return false
      if (wantCombos === null) return false
      if (wantCombos === true && comboEntries.length === 0) return false
      return true
    }
    if (step === 2) {
      if (wantItems === null) return false
      if (wantItems === true) {
        const any = items.some((it) => it.variety && (Number(it.quantity) || 0) > 0)
        if (!any) return false
      }
      // Must have at least ONE thing to sell (either combos OR items).
      if (!wantCombos && !wantItems) return false
      if (allLines.length === 0) return false
      return true
    }
    if (step === 3) {
      if (wantBoxes === null) return false
      if (wantBoxes === true && boxes.length === 0) return false
      return true
    }
    if (step === 4) return true
    return true
  }

  const nextStep = () => setStep((s) => Math.min(5, s + 1))
  const prevStep = () => setStep((s) => Math.max(1, s - 1))

  // ── Submit ───────────────────────────────────────────────────────
  const onSubmit = async () => {
    if (allLines.length === 0) return toast.error('El pedido no tiene líneas')
    const payload = {
      clientId,
      // Por doc: dejamos también el cliente principal en una columna aparte
      // para que el dashboard pueda mostrar mayorista/consignatario sin
      // tener que reconstruir la jerarquía.
      principalClientId: principalClientId || null,
      consigneeClientId: consigneeClientId || null,
      date,
      saleDate: date,
      // Fecha y hora de despacho: se piden desde el comienzo del wizard.
      deliveryDate,
      dispatchTime,
      totalUSD,
      totalStems,
      combos: wantCombos
        ? comboEntries.map((e) => ({ comboId: e.comboId, multiplier: e.multiplier }))
        : [],
      items: allLines.map((l) => ({
        variety: l.variety,
        quantity: Number(l.quantity) || 0,
        stemLength: Number(l.stemLength) || 60,
        priceUSD: Number(l.priceUSD) || 0,
        fromCombo: l.fromCombo || null,
      })),
      boxes: wantBoxes
        ? boxes.map((b) => ({
            boxSize: b.boxSize,
            stemsPerBox: Number(b.stemsPerBox) || 0,
            boxCount: Number(b.boxCount) || 1,
            items: b.items.map((it) => ({
              variety: it.variety,
              quantity: Number(it.quantity) || 0,
              stemLength: Number(it.stemLength) || 60,
            })),
          }))
        : [],
    }
    try {
      await create.mutate(payload)
      toast.success('Venta creada')
      setCreating(false)
      resetForm()
      sales.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onCancel = async (id) => {
    if (!confirm('¿Marcar esta venta como cancelada?')) return
    try {
      await cancel.mutate(id)
      toast.success('Venta cancelada')
      sales.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelete = async (id) => {
    if (!confirm('¿Eliminar esta venta? (no borra POs relacionadas)')) return
    try {
      await remove.mutate(id)
      toast.success('Venta eliminada')
      sales.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const openReturnFor = (sale) => {
    setReturnFor(sale)
    setReturnReason('')
    setReturnItems(
      flattenSaleForDisplay(sale).map((l) => ({
        variety: l.variety,
        stemLength: l.stemLength,
        quantity: 0,
        productCondition: 'acceptable',
      })),
    )
  }

  const onEditSubmit = async (payload) => {
    try {
      const totalStems = (payload.items || []).reduce(
        (a, it) => a + (Number(it.quantity) || 0),
        0,
      )
      const totalUSD = (payload.items || []).reduce(
        (a, it) => a + (Number(it.quantity) || 0) * (Number(it.priceUSD) || 0),
        0,
      )
      await update.mutate({
        id: editFor.id,
        payload: {
          ...payload,
          totalStems,
          totalUSD,
        },
      })
      toast.success('Venta actualizada')
      setEditFor(null)
      sales.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const submitReturn = async (e) => {
    e.preventDefault()
    const chosen = returnItems.filter((it) => Number(it.quantity) > 0)
    if (chosen.length === 0) return toast.error('Indica al menos 1 tallo a devolver')
    try {
      await createReturn.mutate({
        type: 'sale',
        referenceId: returnFor.id,
        clientId: returnFor.clientId,
        reason: returnReason,
        items: chosen,
      })
      toast.success('Devolución creada')
      setReturnFor(null)
      sales.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Ventas"
        subtitle="Órdenes de venta — crea paso a paso: combos, items, empaque y precios"
        icon={DollarSign}
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus size={20} />
            <span>Nueva venta</span>
          </Button>
        }
      />

      {sales.loading && <p className="text-gray-500">Cargando…</p>}
      {sales.error && <p className="text-red-600">Error: {sales.error.message}</p>}

      {/* Filtro por cliente ─────────────────────────────────────────── */}
      {sales.data && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-expo-dark">Cliente:</label>
          <Select
            className="max-w-xs"
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
          >
            <option value="">Todos los clientes</option>
            {(clients.data || [])
              .slice()
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.id})
                  {c.parentClientId ? ' — final' : ''}
                </option>
              ))}
          </Select>
          {selectedClientHasChildren && (
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={filterIncludeChildren}
                onChange={(e) => setFilterIncludeChildren(e.target.checked)}
              />
              Incluir ventas a clientes finales (dropshipping)
            </label>
          )}
          {filterClientId && (
            <span className="ml-auto text-xs text-gray-500">
              {visibleSales.length} venta{visibleSales.length === 1 ? '' : 's'}
              {selectedClient ? ` de ${selectedClient.name}` : ''}
            </span>
          )}
        </div>
      )}

      {sales.data && (
        <ExpandableCards
          items={visibleSales}
          getKey={(r) => r.id}
          emptyText="Sin ventas registradas"
          renderHeader={(r) => {
            const delivery = getDeliveryDate(r)
            return (
              <div className="flex items-center gap-4 flex-wrap">
                <span className="font-semibold text-expo-dark">{r.id}</span>
                <span className="text-gray-600">
                  {r.clientName || r.clientId}
                </span>
                {/* Fecha de entrega SIEMPRE visible, aun si la venta está
                    cerrada (delivered/cancelled). El usuario la necesita en
                    el listado para planear la semana sin tener que abrir
                    cada tarjeta. */}
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-expo-primary/10 text-expo-primary text-xs font-semibold whitespace-nowrap"
                  title="Fecha de entrega"
                >
                  <CalendarClock size={14} />
                  {delivery ? fmtDate(delivery) : 'sin fecha'}
                </span>
                <span className="ml-auto font-bold text-expo-dark">
                  USD {fmtMoney(r.totalUSD)}
                </span>
                <span
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    STATUS_PILL[r.status] || 'bg-gray-100 text-gray-700',
                  )}
                >
                  {SALE_STATUS_LABELS[r.status] || r.status}
                </span>
              </div>
            )
          }}
          renderDetails={(r) => {
            const lines = flattenSaleForDisplay(r)
            return (
              <div className="space-y-4 pt-4">
                <CardGrid cols={4}>
                  <CardField
                    label="Cliente"
                    value={r.clientName || r.clientId}
                  />
                  <CardField
                    label="Fecha de venta"
                    value={fmtDate(getSaleDate(r))}
                  />
                  <CardField
                    label="Fecha de entrega"
                    value={fmtDate(getDeliveryDate(r))}
                  />
                  <CardField
                    label="Estado"
                    value={SALE_STATUS_LABELS[r.status] || r.status}
                  />
                  <CardField
                    label="Hora despacho"
                    value={r.dispatchTime || '—'}
                  />
                  <CardField label="Total USD" value={fmtMoney(r.totalUSD)} />
                </CardGrid>

                {Array.isArray(r.combos) && r.combos.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-expo-dark mb-2">
                      Combos incluidos
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.combos.map((c, i) => {
                        const meta = PREDEFINED_COMBOS.find((p) => p.id === c.comboId)
                        return (
                          <span
                            key={i}
                            className="px-3 py-1 rounded-full bg-expo-primary/10 text-expo-primary text-xs font-medium"
                          >
                            {meta?.name || c.comboId} × {c.multiplier || 1}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-semibold text-expo-dark mb-2">Detalle</div>
                  <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {lines.length === 0 && (
                      <div className="px-4 py-3 text-sm text-gray-500">Sin líneas</div>
                    )}
                    {lines.map((l, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-5 gap-2 px-4 py-2 text-sm text-expo-dark"
                      >
                        <span className="font-medium">{l.variety}</span>
                        <span>{l.stemLength}cm</span>
                        <span>{l.quantity} tallos</span>
                        <span>USD {fmtMoney(l.priceUSD)}/u</span>
                        <span className="text-gray-500 text-right">{l.source}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {Array.isArray(r.boxes) && r.boxes.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-expo-dark mb-2">
                      Plan de empaque
                    </div>
                    <div className="space-y-2">
                      {r.boxes.map((b, bi) => (
                        <div
                          key={bi}
                          className="bg-expo-light/40 rounded-xl p-3 text-sm text-expo-dark"
                        >
                          <div className="font-medium mb-1">
                            {BOX_SIZES[b.boxSize] || b.boxSize} × {b.boxCount} (
                            {b.stemsPerBox} tallos/caja)
                          </div>
                          <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                            {(b.items || []).map((it, ii) => (
                              <span key={ii}>
                                {it.variety} {it.stemLength}cm — {it.quantity}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 flex-wrap pt-2">
                  {/* Editar siempre está habilitado — incluso sobre ventas
                      entregadas o canceladas. Caso típico: ajustar una
                      fecha de entrega mal registrada después del cierre. */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setEditFor(r)}
                  >
                    <Pencil size={16} />
                    <span>Editar</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => openReturnFor(r)}
                    disabled={r.status === 'cancelled'}
                  >
                    <RotateCcw size={16} />
                    <span>Crear devolución</span>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onCancel(r.id)}
                    disabled={r.status === 'cancelled'}
                  >
                    Marcar cancelada
                  </Button>
                  <Button type="button" variant="danger" onClick={() => onDelete(r.id)}>
                    <Trash2 size={16} />
                    <span>Eliminar</span>
                  </Button>
                </div>
              </div>
            )
          }}
        />
      )}

      {/* ── Modal: Wizard Nueva venta ───────────────────────────────── */}
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false)
          resetForm()
        }}
        title="Nueva venta"
        size="xl"
      >
        <StepIndicator step={step} />

        <div className="mt-6 space-y-5">
          {step === 1 && (
            <Step1
              clients={clients.data || []}
              principalClientId={principalClientId}
              setPrincipalClientId={setPrincipalClientId}
              consigneeClientId={consigneeClientId}
              setConsigneeClientId={setConsigneeClientId}
              date={date}
              setDate={setDate}
              deliveryDate={deliveryDate}
              setDeliveryDate={setDeliveryDate}
              dispatchTime={dispatchTime}
              setDispatchTime={setDispatchTime}
              wantCombos={wantCombos}
              setWantCombos={setWantCombos}
              comboEntries={comboEntries}
              addComboEntry={addComboEntry}
              removeComboEntry={removeComboEntry}
              patchComboLine={patchComboLine}
              patchComboMultiplier={patchComboMultiplier}
              varieties={varieties.data || []}
              availableCombos={combosApi.data || PREDEFINED_COMBOS}
            />
          )}

          {step === 2 && (
            <Step2
              wantItems={wantItems}
              setWantItems={setWantItems}
              items={items}
              addItem={addItem}
              removeItem={removeItem}
              patchItem={patchItem}
              varieties={varieties.data || []}
              comboLinesPresent={wantCombos === true && comboEntries.length > 0}
            />
          )}

          {step === 3 && (
            <Step3
              wantBoxes={wantBoxes}
              setWantBoxes={setWantBoxes}
              boxes={boxes}
              setBoxes={setBoxes}
              addPacking={addPacking}
              updatePacking={updatePacking}
              removeBox={removeBox}
              allLines={allLines}
              totalStems={totalStems}
              comboEntries={comboEntries}
              wantCombos={wantCombos}
            />
          )}

          {step === 4 && (
            <Step4
              wantBoxes={wantBoxes}
              boxes={boxes}
              totalBoxedStems={totalBoxedStems}
              totalStems={totalStems}
              allLines={allLines}
            />
          )}

          {step === 5 && (
            <Step5
              allLines={allLines}
              setPriceForLine={setPriceForLine}
              totalUSD={totalUSD}
              totalStems={totalStems}
            />
          )}
        </div>

        {/* Wizard nav */}
        <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={prevStep}
            disabled={step === 1}
          >
            <ChevronLeft size={16} />
            <span>Atrás</span>
          </Button>
          <div className="text-sm text-gray-500">
            Paso {step} de {STEPS.length}
          </div>
          {step < 5 ? (
            <Button type="button" onClick={nextStep} disabled={!canAdvance()}>
              <span>Siguiente</span>
              <ChevronRight size={16} />
            </Button>
          ) : (
            <Button type="button" onClick={onSubmit} disabled={create.loading}>
              <Check size={16} />
              <span>{create.loading ? 'Creando…' : 'Crear venta'}</span>
            </Button>
          )}
        </div>
      </Modal>

      {/* ── Modal: Editar venta ─────────────────────────────────────── */}
      <EditSaleModal
        sale={editFor}
        clients={clients.data || []}
        varieties={varieties.data || []}
        onClose={() => setEditFor(null)}
        onSubmit={onEditSubmit}
        saving={update.loading}
      />

      {/* ── Modal: Devolución desde venta ──────────────────────────── */}
      <Modal
        open={Boolean(returnFor)}
        onClose={() => setReturnFor(null)}
        title={returnFor ? `Nueva devolución — ${returnFor.id}` : ''}
        size="lg"
      >
        {returnFor && (
          <form onSubmit={submitReturn} className="space-y-4">
            <Field label="Motivo">
              <Input
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Razón del cliente"
              />
            </Field>
            <div>
              <div className="text-sm font-medium text-expo-dark mb-2">
                Items a devolver (cantidad)
              </div>
              <div className="space-y-2">
                {returnItems.map((it, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center bg-expo-light/50 p-3 rounded-xl"
                  >
                    <div className="col-span-4 text-sm font-medium text-expo-dark">
                      {it.variety}
                    </div>
                    <div className="col-span-2 text-xs text-gray-500">{it.stemLength}cm</div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min="0"
                        value={it.quantity}
                        onChange={(e) =>
                          setReturnItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, quantity: e.target.value } : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="col-span-3">
                      <Select
                        value={it.productCondition}
                        onChange={(e) =>
                          setReturnItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, productCondition: e.target.value } : r,
                            ),
                          )
                        }
                      >
                        <option value="acceptable">Aceptable</option>
                        <option value="damaged">Dañado</option>
                        <option value="rotten">Podrido</option>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setReturnFor(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createReturn.loading}>
                {createReturn.loading ? 'Creando…' : 'Crear devolución'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step indicator
// ──────────────────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((s, i) => {
        const done = step > s.id
        const active = step === s.id
        return (
          <div key={s.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
                  done && 'bg-expo-primary text-white',
                  active && 'bg-expo-primary/20 text-expo-primary ring-2 ring-expo-primary',
                  !done && !active && 'bg-gray-100 text-gray-500',
                )}
              >
                {done ? <Check size={16} /> : s.id}
              </div>
              <div
                className={cn(
                  'mt-1 text-[11px] text-center leading-tight max-w-[100px]',
                  active ? 'text-expo-dark font-medium' : 'text-gray-500',
                )}
              >
                {s.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'h-0.5 flex-1 mx-1 -mt-6',
                  done ? 'bg-expo-primary' : 'bg-gray-200',
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// YesNo — paired radio cards
// ──────────────────────────────────────────────────────────────────────
function YesNo({ value, onChange, yesLabel, noLabel }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={cn(
          'p-4 rounded-xl border-2 text-left transition-all',
          value === true
            ? 'border-expo-primary bg-expo-primary/5 text-expo-dark'
            : 'border-gray-200 hover:border-gray-300 text-gray-700',
        )}
      >
        <div className="font-semibold mb-1">Sí</div>
        <div className="text-xs text-gray-500">{yesLabel}</div>
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={cn(
          'p-4 rounded-xl border-2 text-left transition-all',
          value === false
            ? 'border-expo-primary bg-expo-primary/5 text-expo-dark'
            : 'border-gray-200 hover:border-gray-300 text-gray-700',
        )}
      >
        <div className="font-semibold mb-1">No</div>
        <div className="text-xs text-gray-500">{noLabel}</div>
      </button>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step 1 — cliente + combos
// ──────────────────────────────────────────────────────────────────────
function Step1({
  clients,
  principalClientId,
  setPrincipalClientId,
  consigneeClientId,
  setConsigneeClientId,
  date,
  setDate,
  deliveryDate,
  setDeliveryDate,
  dispatchTime,
  setDispatchTime,
  wantCombos,
  setWantCombos,
  comboEntries,
  addComboEntry,
  removeComboEntry,
  patchComboLine,
  patchComboMultiplier,
  varieties,
  availableCombos,
}) {
  const [pick, setPick] = useState('')

  // Doc: el cliente que se pone es el Principal; si tiene consignatarios
  // ligados, se puede elegir uno como destino real del envío.
  const principalClients = (clients || []).filter((c) => !c.parentClientId)
  const consigneeOptions = (clients || []).filter(
    (c) => c.parentClientId === principalClientId,
  )

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Cliente Principal" required>
          <Select
            value={principalClientId}
            onChange={(e) => {
              setPrincipalClientId(e.target.value)
              setConsigneeClientId('')
            }}
            required
          >
            <option value="">Selecciona un cliente…</option>
            {principalClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.id})
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Consignatario (opcional)"
          hint={
            principalClientId && consigneeOptions.length === 0
              ? 'Este cliente no tiene consignatarios ligados.'
              : 'Si aplica, selecciona el consignatario al que se enviará.'
          }
        >
          <Select
            value={consigneeClientId}
            onChange={(e) => setConsigneeClientId(e.target.value)}
            disabled={!principalClientId || consigneeOptions.length === 0}
          >
            <option value="">— Envío al Principal —</option>
            {consigneeOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.id})
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Fecha de venta">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Fecha de despacho" required>
          <Input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            required
          />
        </Field>
        <Field label="Hora de despacho">
          <Input
            type="time"
            value={dispatchTime}
            onChange={(e) => setDispatchTime(e.target.value)}
          />
        </Field>
      </div>

      <div>
        <div className="text-sm font-semibold text-expo-dark mb-2">
          ¿Quieres agregar combos preestablecidos?
        </div>
        <YesNo
          value={wantCombos}
          onChange={setWantCombos}
          yesLabel="Elige uno o varios combos; puedes editar cantidades y precios."
          noLabel="Iremos directo a los items individuales."
        />
      </div>

      {wantCombos === true && (
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Añadir combo">
                <Select value={pick} onChange={(e) => setPick(e.target.value)}>
                  <option value="">Selecciona un combo…</option>
                  {availableCombos.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} — {c.description}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={!pick}
              onClick={() => {
                addComboEntry(pick)
                setPick('')
              }}
            >
              <Plus size={16} />
              <span>Añadir</span>
            </Button>
          </div>

          {comboEntries.length === 0 && (
            <p className="text-sm text-gray-500 italic">
              Aún no has agregado ningún combo. Usa el selector de arriba.
            </p>
          )}

          {comboEntries.map((entry, ei) => {
            const entryStems = entry.items.reduce(
              (a, it) => a + (Number(it.quantity) || 0),
              0,
            )
            const entrySubtotal = entry.items.reduce(
              (a, it) => a + (Number(it.quantity) || 0) * (Number(it.priceUSD) || 0),
              0,
            )
            return (
              <div key={ei} className="bg-expo-light/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-semibold text-expo-dark">{entry.comboLabel}</div>
                  <div className="text-xs text-gray-500">× multiplicador</div>
                  <Input
                    type="number"
                    min="1"
                    value={entry.multiplier}
                    onChange={(e) => patchComboMultiplier(ei, e.target.value)}
                    className="w-20"
                  />
                  <div className="ml-auto text-xs text-gray-600">
                    {entryStems} tallos · USD {fmtMoney(entrySubtotal)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeComboEntry(ei)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 px-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    <div className="col-span-4">Variedad</div>
                    <div className="col-span-3">Cantidad (tallos)</div>
                    <div className="col-span-2">Longitud (cm)</div>
                    <div className="col-span-3">Precio (USD / tallo)</div>
                  </div>
                  {entry.items.map((it, li) => (
                    <div
                      key={li}
                      className="grid grid-cols-12 gap-2 bg-white p-2 rounded-lg border border-gray-200"
                    >
                      <div className="col-span-4">
                        <Select
                          value={it.variety}
                          onChange={(e) =>
                            patchComboLine(ei, li, { variety: e.target.value })
                          }
                        >
                          <option value="">Variedad…</option>
                          {varieties.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          min="0"
                          value={it.quantity}
                          onChange={(e) =>
                            patchComboLine(ei, li, { quantity: e.target.value })
                          }
                          placeholder="Tallos"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          value={it.stemLength}
                          onChange={(e) =>
                            patchComboLine(ei, li, { stemLength: e.target.value })
                          }
                          placeholder="cm"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.priceUSD}
                          onChange={(e) =>
                            patchComboLine(ei, li, { priceUSD: e.target.value })
                          }
                          placeholder="USD/tallo"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step 2 — individual items
// ──────────────────────────────────────────────────────────────────────
function Step2({
  wantItems,
  setWantItems,
  items,
  addItem,
  removeItem,
  patchItem,
  varieties,
  comboLinesPresent,
}) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-expo-dark mb-2">
          ¿Quieres agregar items individuales?
        </div>
        <YesNo
          value={wantItems}
          onChange={setWantItems}
          yesLabel="Agrega variedades sueltas (variedad, cantidad, longitud)."
          noLabel={
            comboLinesPresent
              ? 'Usaremos sólo los combos que ya agregaste.'
              : 'Debes agregar al menos combos o items para continuar.'
          }
        />
      </div>

      {wantItems === true && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-expo-dark">Líneas</span>
            <button
              type="button"
              onClick={addItem}
              className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
            >
              <Plus size={16} />
              Añadir línea
            </button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              <div className="col-span-5">Variedad</div>
              <div className="col-span-3">Cantidad (tallos)</div>
              <div className="col-span-3">Longitud (cm)</div>
              <div className="col-span-1" />
            </div>
            {items.map((item, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-2 items-start bg-expo-light/50 p-3 rounded-xl"
              >
                <div className="col-span-5">
                  <Select
                    value={item.variety}
                    onChange={(e) => patchItem(idx, { variety: e.target.value })}
                  >
                    <option value="">Variedad…</option>
                    {varieties.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    min="0"
                    value={item.quantity}
                    onChange={(e) => patchItem(idx, { quantity: e.target.value })}
                    placeholder="Tallos"
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    min="0"
                    value={item.stemLength}
                    onChange={(e) => patchItem(idx, { stemLength: e.target.value })}
                    placeholder="cm"
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Los precios por tallo los definirás en el paso 5.
          </p>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step 3 — packing plan (guided flow)
//
// Flujo:
//   1. Arriba: "übersicht" de tallos pendientes por empacar agrupados por
//      (variedad, longitud). Verde cuando ya está 100% empacado.
//   2. Abajo: tarjetas de cada grupo de empaque ya definido. Cada tarjeta
//      corresponde a UNA variedad con UN tipo de caja.
//   3. Formulario "Nuevo empaque":
//        - Variedad (dropdown; lista solo las que aún tienen tallos pendientes)
//        - Tipo de caja
//        - Tallos por caja
//        - Cantidad de cajas (precargado a max(1, floor(pendientes/tallosPorCaja)))
//        - Auto-calcula "tallos empacados = tallosPorCaja * cajas" y lo compara
//          contra pendientes. Añadir sólo es posible si no excede.
//   4. Cuando todas las variedades quedan en 0 pendientes, mostramos banner
//      "Todo empacado".
// ──────────────────────────────────────────────────────────────────────
function Step3({
  wantBoxes,
  setWantBoxes,
  boxes,
  setBoxes,
  addPacking,
  updatePacking,
  removeBox,
  allLines,
  totalStems,
  comboEntries,
  wantCombos,
}) {
  /**
   * Preset desde combos: cuando hay combos preestablecidos y aún no hay
   * cajas, generamos un empaque por defecto — una caja por combo entry,
   * con multiplicador=boxCount y todos los items del combo dentro. Así
   * el operario solo tiene que confirmar o ajustar.
   *
   * El preset se ofrece como botón ("Generar empaque del combo") — no se
   * aplica automático para que el usuario no pierda el control.
   */
  const presetAvailable =
    wantCombos === true &&
    Array.isArray(comboEntries) &&
    comboEntries.length > 0 &&
    boxes.length === 0
  const applyComboPreset = () => {
    // Aplana TODOS los combo entries y TODAS sus cajas por defecto a
    // una sola lista de cajas para boxes[]. Cada caja del combo se
    // multiplica por (boxCount × multiplier) para reflejar cuántas
    // cajas reales se necesitan.
    const next = []
    for (const entry of comboEntries) {
      const mult = Math.max(1, Number(entry.multiplier) || 1)
      // defaultPacking puede ser:
      //   - array de cajas (formato canónico actual)
      //   - objeto único {boxSize, boxCount} (legacy) → 1 caja con todos
      //     los items del combo
      //   - undefined → fallback a 1 fullbox con todos los items
      let packingList = []
      if (Array.isArray(entry.defaultPacking)) {
        packingList = entry.defaultPacking
      } else if (entry.defaultPacking && typeof entry.defaultPacking === 'object') {
        packingList = [
          {
            boxSize: entry.defaultPacking.boxSize || 'fullbox',
            boxCount: Number(entry.defaultPacking.boxCount) || 1,
            items: entry.items,
          },
        ]
      } else {
        packingList = [
          { boxSize: 'fullbox', boxCount: 1, items: entry.items },
        ]
      }
      for (const dpBox of packingList) {
        const items = (dpBox.items || []).map((it) => ({
          variety: it.variety,
          stemLength: Number(it.stemLength) || 0,
          quantity: Number(it.quantity) || 0,
        }))
        if (items.length === 0) continue
        const totalBoxes = Math.max(
          1,
          (Number(dpBox.boxCount) || 1) * mult,
        )
        next.push({
          boxSize: dpBox.boxSize || 'fullbox',
          boxCount: totalBoxes,
          stemsPerBox: items.reduce(
            (a, it) => a + (Number(it.quantity) || 0),
            0,
          ),
          items,
        })
      }
    }
    setBoxes(next)
  }

  // Totales pedidos por (variedad, longitud).
  const pendingMap = useMemo(() => {
    const m = new Map()
    for (const l of allLines) {
      if (!l.variety) continue
      const k = lineKey(l.variety, l.stemLength)
      if (!m.has(k)) {
        m.set(k, {
          key: k,
          variety: l.variety,
          stemLength: Number(l.stemLength) || 0,
          total: 0,
        })
      }
      m.get(k).total += Number(l.quantity) || 0
    }
    return m
  }, [allLines])

  // Tallos ya asignados a cajas, por (variedad, longitud).
  const packedMap = useMemo(() => {
    const m = new Map()
    for (const b of boxes) {
      for (const it of b.items || []) {
        if (!it.variety) continue
        const k = lineKey(it.variety, it.stemLength)
        const add = (Number(it.quantity) || 0) * (Number(b.boxCount) || 0)
        m.set(k, (m.get(k) || 0) + add)
      }
    }
    return m
  }, [boxes])

  // Lista combinada: toda variedad pedida + cuánto empacado + cuánto falta.
  const overview = useMemo(() => {
    const rows = []
    for (const p of pendingMap.values()) {
      const packed = packedMap.get(p.key) || 0
      rows.push({
        ...p,
        packed,
        remaining: p.total - packed,
      })
    }
    rows.sort((a, b) => (b.remaining - a.remaining) || a.variety.localeCompare(b.variety))
    return rows
  }, [pendingMap, packedMap])

  const allPacked =
    wantBoxes === true && overview.length > 0 && overview.every((r) => r.remaining === 0)
  const anyOverPacked = overview.some((r) => r.remaining < 0)

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-expo-dark mb-2">
          ¿Cómo vas a empacar este pedido?
        </div>
        <YesNo
          value={wantBoxes}
          onChange={setWantBoxes}
          yesLabel="Empaca variedad por variedad: elige tipo de caja y tallos por caja."
          noLabel="Sin plan de empaque — saltamos al paso de precios."
        />
        <p className="text-xs text-gray-500 mt-2">
          Total de tallos por empacar (desde pasos 1 y 2): <strong>{totalStems}</strong>
        </p>
      </div>

      {wantBoxes === true && (
        <div className="space-y-5">
          {/* Übersicht — pendientes por empacar */}
          <PendingOverview overview={overview} allPacked={allPacked} anyOver={anyOverPacked} />

          {/* Preset desde combo (botón) */}
          {presetAvailable && (
            <div className="bg-expo-yellow/20 border border-expo-yellow rounded-xl p-3 flex items-center gap-3 flex-wrap">
              <Package size={18} className="text-expo-dark" />
              <span className="text-sm text-expo-dark">
                Tienes combos preestablecidos. ¿Generar el empaque automáticamente?
                (Cada combo = una caja con todas sus variedades. Podrás editarlo.)
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={applyComboPreset}
                className="ml-auto"
              >
                Generar empaque del combo
              </Button>
            </div>
          )}

          {/* Grupos de empaque ya añadidos */}
          {boxes.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-expo-dark mb-2">
                Grupos de empaque ({boxes.length})
              </div>
              <div className="space-y-2">
                {boxes.map((b, bi) => (
                  <BoxRow
                    key={bi}
                    box={b}
                    index={bi}
                    onUpdate={(patch) => updatePacking(bi, patch)}
                    onRemove={() => removeBox(bi)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Formulario nuevo empaque */}
          {!allPacked && <AddPackingForm overview={overview} addPacking={addPacking} />}
          {allPacked && (
            <div className="bg-expo-primary/10 border border-expo-primary/30 rounded-xl p-4 text-center">
              <Check size={24} className="mx-auto text-expo-primary mb-1" />
              <div className="font-semibold text-expo-dark">Todo empacado</div>
              <p className="text-xs text-gray-600">
                Todas las variedades están asignadas a cajas. Avanza al siguiente paso.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Sub-components for Step 3 ───────────────────────────────────────────
function PendingOverview({ overview, allPacked, anyOver }) {
  if (overview.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
        No hay variedades para empacar. Vuelve al paso 1 o 2 para agregar líneas.
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-expo-dark">Pendiente por empacar</div>
        {allPacked && (
          <span className="text-xs font-medium text-expo-primary flex items-center gap-1">
            <Check size={14} />
            Todo empacado
          </span>
        )}
        {anyOver && (
          <span className="text-xs font-medium text-red-600">
            Una variedad quedó sobre-empacada
          </span>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-2 bg-expo-light/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <div className="col-span-4">Variedad</div>
          <div className="col-span-2">Longitud</div>
          <div className="col-span-2 text-right">Pedidos</div>
          <div className="col-span-2 text-right">Empacados</div>
          <div className="col-span-2 text-right">Pendientes</div>
        </div>
        <div className="divide-y divide-gray-100">
          {overview.map((row) => {
            const done = row.remaining === 0
            const over = row.remaining < 0
            return (
              <div
                key={row.key}
                className={cn(
                  'grid grid-cols-12 gap-2 px-3 py-2 text-sm items-center',
                  done && 'bg-expo-primary/5',
                  over && 'bg-red-50',
                )}
              >
                <div className="col-span-4 font-medium text-expo-dark">{row.variety}</div>
                <div className="col-span-2 text-gray-600">{row.stemLength}cm</div>
                <div className="col-span-2 text-right text-expo-dark">{row.total}</div>
                <div className="col-span-2 text-right text-gray-700">{row.packed}</div>
                <div
                  className={cn(
                    'col-span-2 text-right font-semibold',
                    done && 'text-expo-primary',
                    over && 'text-red-600',
                    !done && !over && 'text-expo-dark',
                  )}
                >
                  {row.remaining}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Resumen + editor inline de una caja existente. El usuario puede
 * cambiar tipo de caja, cantidad de cajas, y agregar / quitar /
 * modificar variedades dentro de la caja. Útil sobre todo cuando el
 * empaque se generó automáticamente desde un combo.
 */
function BoxRow({ box, index, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false)
  const [boxSize, setBoxSize] = useState(box.boxSize)
  const [boxCount, setBoxCount] = useState(box.boxCount)
  const [items, setItems] = useState(
    (box.items || []).map((it) => ({
      variety: it.variety || '',
      stemLength: Number(it.stemLength) || 0,
      quantity: Number(it.quantity) || 0,
    })),
  )

  // Si la prop cambia desde afuera (p.ej. preset), refrescar.
  useEffect(() => {
    setBoxSize(box.boxSize)
    setBoxCount(box.boxCount)
    setItems(
      (box.items || []).map((it) => ({
        variety: it.variety || '',
        stemLength: Number(it.stemLength) || 0,
        quantity: Number(it.quantity) || 0,
      })),
    )
  }, [box])

  const totalStemsGroup = items.reduce(
    (a, it) => a + (Number(it.quantity) || 0),
    0,
  ) * (Number(boxCount) || 0)

  const onSave = () => {
    onUpdate({ boxSize, boxCount, items })
    setEditing(false)
  }
  const patchItem = (i, patch) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const addItemRow = () =>
    setItems((l) => [...l, { variety: '', stemLength: 60, quantity: 0 }])
  const removeItemRow = (i) =>
    setItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))

  if (!editing) {
    // Vista compacta — resume todo lo que va en la caja.
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3 flex-wrap">
        <Package size={18} className="text-expo-primary mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-expo-dark">
              {BOX_SIZES[boxSize] || boxSize}
            </span>
            <span className="text-gray-500 text-sm">
              × {boxCount} caja{boxCount === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="text-sm text-expo-dark mt-1 list-disc list-inside">
            {(box.items || []).map((it, i) => (
              <li key={i}>
                <strong>{it.quantity}</strong> {it.variety}{' '}
                <span className="text-gray-500">{it.stemLength}cm</span>
              </li>
            ))}
          </ul>
        </div>
        <span className="font-semibold text-expo-primary text-sm whitespace-nowrap">
          {totalStemsGroup} tallos
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="p-1.5 rounded-lg text-expo-primary hover:bg-expo-primary/10"
          title="Editar caja"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
          title="Eliminar caja"
        >
          <Trash2 size={14} />
        </button>
      </div>
    )
  }

  // Modo edición — caja completa con sus items modificables.
  return (
    <div className="bg-white border-2 border-expo-primary rounded-xl p-4 space-y-3">
      <div className="text-sm font-semibold text-expo-dark">
        Editando caja #{index + 1}
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6">
          <Field label="Tipo de caja">
            <Select value={boxSize} onChange={(e) => setBoxSize(e.target.value)}>
              {Object.entries(BOX_SIZES).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="col-span-6">
          <Field
            label="Cantidad de cajas iguales"
            hint="Cuántas cajas como esta (con el mismo contenido)."
          >
            <Input
              type="number"
              min="1"
              value={boxCount}
              onChange={(e) => setBoxCount(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-expo-dark">
            Variedades dentro de cada caja
          </span>
          <button
            type="button"
            onClick={addItemRow}
            className="text-sm text-expo-primary font-medium flex items-center gap-1"
          >
            <Plus size={14} />
            Añadir variedad
          </button>
        </div>
        <PackingItemsEditor items={items} patchItem={patchItem} removeRow={removeItemRow} />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
          Cancelar
        </Button>
        <Button type="button" onClick={onSave}>
          Guardar caja
        </Button>
      </div>
    </div>
  )
}

/**
 * Editor de los items que van DENTRO de una caja (lista variable).
 * Se reusa entre BoxRow (edición) y AddPackingForm (creación).
 */
function PackingItemsEditor({ items, patchItem, removeRow }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 px-3 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        <div className="col-span-6">Variedad</div>
        <div className="col-span-2">Longitud cm</div>
        <div className="col-span-3">Tallos/caja</div>
        <div className="col-span-1" />
      </div>
      {items.map((it, idx) => (
        <div
          key={idx}
          className="grid grid-cols-12 gap-2 bg-expo-light/50 p-2 rounded-xl items-center"
        >
          <div className="col-span-6">
            <Input
              value={it.variety}
              onChange={(e) => patchItem(idx, { variety: e.target.value })}
              placeholder="Variedad"
            />
          </div>
          <div className="col-span-2">
            <Input
              type="number"
              min="0"
              value={it.stemLength}
              onChange={(e) => patchItem(idx, { stemLength: e.target.value })}
            />
          </div>
          <div className="col-span-3">
            <Input
              type="number"
              min="0"
              value={it.quantity}
              onChange={(e) => patchItem(idx, { quantity: e.target.value })}
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <button
              type="button"
              onClick={() => removeRow(idx)}
              disabled={items.length === 1}
              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Formulario para crear una nueva caja con MÚLTIPLES variedades.
 *
 * El usuario:
 *   - Elige tipo de caja y cuántas cajas iguales serán
 *   - Va añadiendo filas variety/stemLength/cantidad
 *   - "Añadir al plan" empuja la caja completa a boxes[]
 *
 * "Sugerir desde pendientes": botón que prellena las filas con todo lo
 * que aún falta por empacar (variedades con remaining > 0). Útil para
 * armar rápido una caja mixta sin escribir todo a mano.
 */
function AddPackingForm({ overview, addPacking }) {
  const selectable = overview.filter((r) => r.remaining > 0)
  const [boxSize, setBoxSize] = useState('fullbox')
  const [boxCount, setBoxCount] = useState(1)
  const [items, setItems] = useState([{ variety: '', stemLength: 60, quantity: 0 }])

  const patchItem = (i, patch) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  const addItemRow = () =>
    setItems((l) => [...l, { variety: '', stemLength: 60, quantity: 0 }])
  const removeItemRow = (i) =>
    setItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))

  const suggestFromPending = () => {
    if (selectable.length === 0) return
    setItems(
      selectable.map((r) => ({
        variety: r.variety,
        stemLength: r.stemLength,
        quantity: r.remaining, // por defecto poner todo lo pendiente
      })),
    )
    setBoxCount(1)
  }

  const cleaned = items.filter(
    (it) => it.variety && (Number(it.quantity) || 0) > 0,
  )
  const stemsPerBox = cleaned.reduce((a, it) => a + (Number(it.quantity) || 0), 0)
  const totalStems = stemsPerBox * (Number(boxCount) || 0)

  // Validar que no se exceda lo pendiente por (variedad, longitud).
  const exceed = (() => {
    const map = new Map()
    for (const it of cleaned) {
      const k = lineKey(it.variety, it.stemLength)
      map.set(k, (map.get(k) || 0) + Number(it.quantity) * (Number(boxCount) || 0))
    }
    const issues = []
    for (const r of overview) {
      const k = r.key
      const want = map.get(k) || 0
      if (want > r.remaining) {
        issues.push({ variety: r.variety, stemLength: r.stemLength, over: want - r.remaining })
      }
    }
    return issues
  })()

  const canAdd = cleaned.length > 0 && (Number(boxCount) || 0) > 0 && exceed.length === 0

  const onAdd = () => {
    if (cleaned.length === 0) return toast.error('Agrega al menos una variedad')
    if ((Number(boxCount) || 0) <= 0) return toast.error('Cantidad de cajas inválida')
    if (exceed.length > 0) return toast.error('Excede los tallos pendientes')
    addPacking({ boxSize, boxCount: Number(boxCount), items: cleaned })
    // Reset para la siguiente caja, manteniendo el tipo.
    setItems([{ variety: '', stemLength: 60, quantity: 0 }])
    setBoxCount(1)
  }

  if (selectable.length === 0 && overview.length === 0) return null

  return (
    <div className="bg-expo-light/50 border border-expo-primary/30 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-expo-dark flex items-center gap-2">
          <Plus size={16} />
          <span>Nueva caja (puede contener varias variedades)</span>
        </div>
        {selectable.length > 0 && (
          <Button type="button" variant="secondary" onClick={suggestFromPending}>
            Llenar con pendientes
          </Button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7">
          <Field label="Tipo de caja">
            <Select value={boxSize} onChange={(e) => setBoxSize(e.target.value)}>
              {Object.entries(BOX_SIZES).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="col-span-5">
          <Field
            label="Cantidad de cajas iguales"
            hint="N cajas idénticas (mismo contenido)."
          >
            <Input
              type="number"
              min="1"
              value={boxCount}
              onChange={(e) => setBoxCount(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-expo-dark">
            Variedades dentro de cada caja
          </span>
          <button
            type="button"
            onClick={addItemRow}
            className="text-sm text-expo-primary font-medium flex items-center gap-1"
          >
            <Plus size={14} />
            Añadir variedad
          </button>
        </div>
        <PackingItemsEditor items={items} patchItem={patchItem} removeRow={removeItemRow} />
      </div>

      <div
        className={cn(
          'rounded-lg px-3 py-2 text-sm flex items-center justify-between flex-wrap gap-2',
          exceed.length > 0
            ? 'bg-red-50 text-red-700'
            : 'bg-white border border-gray-200 text-expo-dark',
        )}
      >
        <span>
          {boxCount || 0} caja{(boxCount || 0) === 1 ? '' : 's'} de{' '}
          {BOX_SIZES[boxSize] || boxSize} · {stemsPerBox} tallos por caja ·{' '}
          <strong>{totalStems}</strong> tallos en total
        </span>
        {exceed.length > 0 && (
          <span className="text-xs">
            Excede en{' '}
            {exceed
              .map((e) => `${e.over} ${e.variety} ${e.stemLength}cm`)
              .join(', ')}
          </span>
        )}
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={onAdd} disabled={!canAdd}>
          <Plus size={16} />
          <span>Añadir caja al plan</span>
        </Button>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step 4 — read-only overview of boxes
// ──────────────────────────────────────────────────────────────────────
function Step4({ wantBoxes, boxes, totalBoxedStems, totalStems, allLines }) {
  if (!wantBoxes) {
    return (
      <div className="bg-expo-light/50 p-6 rounded-xl text-center">
        <Package size={32} className="mx-auto text-gray-400 mb-2" />
        <div className="font-medium text-expo-dark">Sin plan de empaque</div>
        <p className="text-sm text-gray-500 mt-1">
          Este pedido se creará sin cajas asignadas. Avanza al último paso para revisar precios.
        </p>
      </div>
    )
  }

  const mismatch = totalBoxedStems !== totalStems

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-expo-light/50 rounded-xl p-3 text-center">
          <div className="text-xs text-gray-500">Tallos en pedido</div>
          <div className="text-2xl font-bold text-expo-dark">{totalStems}</div>
        </div>
        <div
          className={cn(
            'rounded-xl p-3 text-center',
            mismatch ? 'bg-red-50' : 'bg-expo-primary/10',
          )}
        >
          <div className="text-xs text-gray-500">Tallos empacados</div>
          <div
            className={cn(
              'text-2xl font-bold',
              mismatch ? 'text-red-600' : 'text-expo-primary',
            )}
          >
            {totalBoxedStems}
          </div>
        </div>
      </div>
      {mismatch && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
          Aviso: los tallos empacados ({totalBoxedStems}) no coinciden con los tallos del pedido
          ({totalStems}). Puedes seguir — el plan de empaque se guarda como informativo — pero lo
          ideal es que coincidan.
        </div>
      )}

      <div className="space-y-3">
        {boxes.map((box, bi) => {
          const perBox = box.items.reduce((a, it) => a + (Number(it.quantity) || 0), 0)
          const totalInGroup = perBox * (Number(box.boxCount) || 1)
          return (
            <div key={bi} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <Package size={18} className="text-expo-primary" />
                <span className="font-semibold text-expo-dark">
                  {BOX_SIZES[box.boxSize] || box.boxSize}
                </span>
                <span className="text-sm text-gray-600">
                  {box.stemsPerBox} tallos × {box.boxCount} caja
                  {box.boxCount === 1 ? '' : 's'}
                </span>
                <span className="ml-auto text-sm font-medium text-expo-primary">
                  {totalInGroup} tallos
                </span>
              </div>
              <div className="bg-expo-light/40 rounded-lg divide-y divide-white">
                {box.items.map((it, ii) => (
                  <div
                    key={ii}
                    className="grid grid-cols-3 gap-2 p-2 text-sm text-expo-dark"
                  >
                    <span className="font-medium">{it.variety || '—'}</span>
                    <span>{it.stemLength}cm</span>
                    <span className="text-right">{it.quantity} tallos/caja</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {allLines.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-expo-dark mb-2">
            Resumen del contenido (combos + items)
          </div>
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 text-sm">
            {allLines.map((l, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 p-2 text-expo-dark">
                <span className="font-medium">{l.variety}</span>
                <span>{l.stemLength}cm</span>
                <span>{l.quantity} tallos</span>
                <span className="text-gray-500 text-right">
                  {l.fromCombo ? `combo ${l.fromCombo}` : 'individual'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Edit modal — una venta siempre se puede editar, incluso cerrada.
//
// Mantiene intencionalmente un scope más chico que el wizard de creación:
// datos de cabecera + líneas (variety/qty/stemLength/price) + estado. El
// plan de empaque (boxes[]) se preserva tal cual — si hay que reempacar,
// se borra la venta y se crea nueva. Para el 95% de las ediciones reales
// (fecha mal registrada, cambio de cliente, ajuste de cantidad) con esto
// alcanza y no expone al usuario a la complejidad de los 5 pasos.
// ──────────────────────────────────────────────────────────────────────
/**
 * Sanitiza el valor de un input type="time".
 *
 * El input HTML solo acepta HH:mm (24h). Si los datos guardados traen
 * formatos legacy como "3:05 PM", " 09:30", o " 09:30:00", el navegador
 * lanza el clásico "valor no válido" y el form no permite guardar.
 *
 * Esta función intenta normalizar antes de pintar el input, evitando esa
 * regresión que el cliente reportó al editar ventas existentes.
 */
function sanitizeTime(raw) {
  if (!raw) return ''
  const t = String(raw).trim()
  // ya en HH:mm
  if (/^\d{2}:\d{2}$/.test(t)) return t
  // HH:mm:ss → recortar
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t.slice(0, 5)
  // h:mm AM/PM
  const ampm = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/)
  if (ampm) {
    let h = Number(ampm[1])
    const m = ampm[2]
    const tag = ampm[3].toUpperCase()
    if (tag === 'PM' && h < 12) h += 12
    if (tag === 'AM' && h === 12) h = 0
    return `${String(h).padStart(2, '0')}:${m}`
  }
  // h:mm
  const hm = t.match(/^(\d{1,2}):(\d{2})$/)
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2]}`
  return ''
}

function EditSaleModal({ sale, clients, varieties, onClose, onSubmit, saving }) {
  const [clientId, setClientId] = useState('')
  const [saleDate, setSaleDate] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [dispatchTime, setDispatchTime] = useState('')
  const [items, setItems] = useState([])

  // Hidratar el form cada vez que `sale` cambie. Por feedback, en este
  // modal NO se muestra el dropdown de estado: la venta solo puede
  // editarse o cancelarse desde el listado / el dashboard.
  useEffect(() => {
    if (!sale) return
    setClientId(sale.clientId || '')
    setSaleDate(getSaleDate(sale))
    setDeliveryDate(getDeliveryDate(sale))
    setDispatchTime(sanitizeTime(sale.dispatchTime))
    // Clonar items[]. Si no hay items[] pero sí boxes[], expandimos a la
    // lista plana para que el usuario vea lo que hay sin perder datos.
    const rawItems = (sale.items || []).map((it) => ({
      variety: it.variety || '',
      quantity: Number(it.quantity) || 0,
      stemLength: Number(it.stemLength) || 60,
      priceUSD: Number(it.priceUSD) || 0,
      fromCombo: it.fromCombo || null,
    }))
    if (rawItems.length === 0 && Array.isArray(sale.boxes)) {
      const fallback = []
      for (const box of sale.boxes) {
        for (const it of box.items || []) {
          fallback.push({
            variety: it.variety || '',
            quantity:
              (Number(it.quantity) || 0) * (Number(box.boxCount) || 1),
            stemLength: Number(it.stemLength) || 60,
            priceUSD: Number(it.priceUSD) || 0,
            fromCombo: null,
          })
        }
      }
      setItems(fallback)
    } else {
      setItems(rawItems)
    }
  }, [sale])

  const patchItem = (idx, patch) =>
    setItems((l) => l.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  const addRow = () =>
    setItems((l) => [
      ...l,
      { variety: '', quantity: 0, stemLength: 60, priceUSD: 0, fromCombo: null },
    ])
  const removeRow = (idx) => setItems((l) => l.filter((_, i) => i !== idx))

  const subtotal = useMemo(
    () =>
      items.reduce(
        (a, it) =>
          a + (Number(it.quantity) || 0) * (Number(it.priceUSD) || 0),
        0,
      ),
    [items],
  )
  const stems = useMemo(
    () => items.reduce((a, it) => a + (Number(it.quantity) || 0), 0),
    [items],
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!clientId) return toast.error('Selecciona un cliente')
    const cleaned = items
      .filter((it) => it.variety && (Number(it.quantity) || 0) > 0)
      .map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity) || 0,
        stemLength: Number(it.stemLength) || 60,
        priceUSD: Number(it.priceUSD) || 0,
        fromCombo: it.fromCombo || null,
      }))
    if (cleaned.length === 0) {
      return toast.error('La venta debe tener al menos una línea')
    }
    // Mantenemos clientName sincronizado para la tarjeta del listado.
    const client = clients.find((c) => c.id === clientId)
    onSubmit({
      clientId,
      clientName: client?.name || sale.clientName || clientId,
      saleDate,
      deliveryDate,
      dispatchTime,
      // Preserva el status existente (no se edita desde aquí — el cambio
      // de status se hace desde el dashboard con la transición correcta).
      items: cleaned,
    })
  }

  return (
    <Modal
      open={Boolean(sale)}
      onClose={onClose}
      title={sale ? `Editar venta — ${sale.id}` : ''}
      size="xl"
    >
      {sale && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Cliente" required>
              <Select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
              >
                <option value="">Selecciona un cliente…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.id})
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Hora de despacho"
              hint="Formato 24h. Las horas en formato antiguo se normalizan automáticamente."
            >
              <Input
                type="time"
                value={dispatchTime}
                onChange={(e) => setDispatchTime(e.target.value)}
              />
            </Field>
            <Field label="Fecha de venta">
              <Input
                type="date"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
              />
            </Field>
            <Field label="Fecha de entrega" required>
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </Field>
            <div className="flex items-end">
              <div className="w-full rounded-xl bg-expo-primary/5 border border-expo-primary/20 px-3 py-2">
                <div className="text-xs text-gray-500">Totales</div>
                <div className="text-sm font-semibold text-expo-dark">
                  {stems} tallos · USD {fmtMoney(subtotal)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-expo-dark">Líneas</div>
              <button
                type="button"
                onClick={addRow}
                className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Añadir línea
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-4">Variedad</div>
                <div className="col-span-2">Tallos</div>
                <div className="col-span-2">Longitud</div>
                <div className="col-span-3">Precio USD/tallo</div>
                <div className="col-span-1" />
              </div>
              {items.length === 0 && (
                <div className="text-sm text-gray-500 italic px-3 py-2">
                  Sin líneas. Usa "Añadir línea" para empezar.
                </div>
              )}
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-center bg-expo-light/50 p-3 rounded-xl"
                >
                  <div className="col-span-4">
                    <Select
                      value={it.variety}
                      onChange={(e) => patchItem(idx, { variety: e.target.value })}
                    >
                      <option value="">Variedad…</option>
                      {varieties.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) =>
                        patchItem(idx, { quantity: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={it.stemLength}
                      onChange={(e) =>
                        patchItem(idx, { stemLength: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.priceUSD}
                      onChange={(e) =>
                        patchItem(idx, { priceUSD: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                      title="Eliminar línea"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {Array.isArray(sale.boxes) && sale.boxes.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                Esta venta tiene un plan de empaque registrado ({sale.boxes.length}{' '}
                grupo{sale.boxes.length === 1 ? '' : 's'}). Se conserva tal cual —
                editar las líneas no toca el empaque.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              <Check size={16} />
              <span>{saving ? 'Guardando…' : 'Guardar cambios'}</span>
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Step 5 — prices & confirmation
// ──────────────────────────────────────────────────────────────────────
function Step5({ allLines, setPriceForLine, totalUSD, totalStems }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-expo-dark mb-2">
          Ajusta los precios por tallo
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 bg-expo-light/60 px-3 py-2 text-xs font-semibold text-expo-dark">
            <span className="col-span-3">Variedad</span>
            <span className="col-span-2">Longitud</span>
            <span className="col-span-2">Tallos</span>
            <span className="col-span-2">Precio USD</span>
            <span className="col-span-2">Subtotal</span>
            <span className="col-span-1">Origen</span>
          </div>
          <div className="divide-y divide-gray-100">
            {allLines.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500">Sin líneas</div>
            )}
            {allLines.map((l, i) => {
              const subtotal = (Number(l.quantity) || 0) * (Number(l.priceUSD) || 0)
              return (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                  <span className="col-span-3 text-sm font-medium text-expo-dark">
                    {l.variety}
                  </span>
                  <span className="col-span-2 text-sm text-gray-600">{l.stemLength}cm</span>
                  <span className="col-span-2 text-sm">{l.quantity}</span>
                  <span className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.priceUSD}
                      onChange={(e) => setPriceForLine(i, e.target.value)}
                    />
                  </span>
                  <span className="col-span-2 text-sm font-semibold text-expo-dark">
                    USD {fmtMoney(subtotal)}
                  </span>
                  <span className="col-span-1 text-[10px] text-gray-500 text-right truncate">
                    {l.fromCombo ? l.fromCombo : 'item'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="bg-expo-primary/5 border-2 border-expo-primary/30 rounded-xl p-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-600">Totales</div>
          <div className="text-xs text-gray-500">{totalStems} tallos en {allLines.length} línea{allLines.length === 1 ? '' : 's'}</div>
        </div>
        <div className="text-3xl font-bold text-expo-dark">USD {fmtMoney(totalUSD)}</div>
      </div>
    </div>
  )
}
